import { useCallback, useRef } from 'react'
import { useCallStore } from '../stores/useCallStore'
import { useChatStore } from '../stores/useChatStore'
import { useAuthContext } from '../contexts/AuthContext'
import { RealtimeCallClient } from '../services/realtimeCallService'
import {
  getStorageItem,
  setStorageItem,
  removeStorageItem,
} from '../utils/localStorage'
import { useConversationManagement } from './useConversationManagement'
import { useShallow } from 'zustand/react/shallow'

export const useCallActions = () => {
  const { user, token } = useAuthContext()
  const { fetchConversations } = useConversationManagement()

  const { selectedConversation } = useChatStore(
    useShallow(state => ({
      selectedConversation: state.selectedConversation,
    }))
  )
  const {
    isCallLockedByOtherTab,
    isConnecting,
    setCallClient,
    setIsCallActive,
    setIsConnecting,
    setCallStatus,
    setIsMicOn,
    setIsScreenOn,
    setIsStartingScreen,
    appendToCallTranscript,
    setAudioLevel,
    setCallSavedConversationId,
    setShowContinueInChat,
    resetCallState,
  } = useCallStore(
    useShallow(state => ({
      isCallLockedByOtherTab: state.isCallLockedByOtherTab,
      isConnecting: state.isConnecting,
      setCallClient: state.setCallClient,
      setIsCallActive: state.setIsCallActive,
      setIsConnecting: state.setIsConnecting,
      setCallStatus: state.setCallStatus,
      setIsMicOn: state.setIsMicOn,
      setIsScreenOn: state.setIsScreenOn,
      setIsStartingScreen: state.setIsStartingScreen,
      appendToCallTranscript: state.appendToCallTranscript,
      setAudioLevel: state.setAudioLevel,
      setCallSavedConversationId: state.setCallSavedConversationId,
      setShowContinueInChat: state.setShowContinueInChat,
      resetCallState: state.resetCallState,
    }))
  )
  const tabIdRef = useRef(null)

  if (!tabIdRef.current) {
    const stored = sessionStorage.getItem('franca_tab_id')
    if (stored) {
      tabIdRef.current = stored
    } else {
      const newId = `tab_${Math.random().toString(36).slice(2)}`
      sessionStorage.setItem('franca_tab_id', newId)
      tabIdRef.current = newId
    }
  }

  const getCallLockKey = useCallback(() => {
    const userId = user?.id || user?._id || user?.user_id
    return userId ? `franca_call_lock_${userId}` : null
  }, [user])

  const acquireCallLock = useCallback(() => {
    const key = getCallLockKey()
    if (!key) return false

    const existingLock = getStorageItem(key)
    if (existingLock && existingLock.tabId && existingLock.tabId !== tabIdRef.current) {
      return false
    }
    setStorageItem(key, {
      tabId: tabIdRef.current,
      startedAt: Date.now(),
    })
    return true
  }, [getCallLockKey])

  const releaseCallLock = useCallback(() => {
    const key = getCallLockKey()
    if (!key) return
    const existingLock = getStorageItem(key)
    if (existingLock && existingLock.tabId === tabIdRef.current) {
      removeStorageItem(key)
    }
  }, [getCallLockKey])

  const endCall = useCallback(async () => {
    resetCallState()
    fetchConversations()
    setTimeout(() => setCallStatus(''), 2000)
    releaseCallLock()
  }, [resetCallState, releaseCallLock, fetchConversations, setCallStatus])

  const startCall = useCallback(async () => {
    if (isConnecting) return null
    try {
      if (isCallLockedByOtherTab) {
        throw new Error("⚠️ Chiamata già attiva in un'altra scheda")
      }

      if (!acquireCallLock()) {
        throw new Error("⚠️ Chiamata già attiva in un'altra scheda")
      }

      if (!token) {
        throw new Error('Token non disponibile')
      }

      setIsConnecting(true)
      setCallStatus('Connessione in corso...')
      setShowContinueInChat(false)
      setCallSavedConversationId(null)

      const client = new RealtimeCallClient({
        token,
        conversationId: selectedConversation?.id || null,
      })

      client.onTranscript = text => appendToCallTranscript(text)
      client.onInfo = msg => setCallStatus(msg)

      client.onError = msg => {
        console.error('❌ Call error:', msg)
        setCallStatus(`❌ ${msg}`)
        setIsConnecting(false)

        if (msg.includes('GEMINI_API_KEY') || msg.includes('Server misconfigured')) {
          setTimeout(() => endCall(), 3000)
        }
      }

      client.onState = state => {
        if (state === 'connected') {
          setCallStatus('✅ Connesso - Avvio microfono...')
        } else if (state === 'closed') {
          setCallStatus('❌ Connessione chiusa')
          setIsConnecting(false)
          setTimeout(() => endCall(), 2000)
        } else {
          setCallStatus(state)
        }
      }

      client.onConversationSaved = conversationId => {
        console.log('💾 Conversation saved:', conversationId)
        setCallSavedConversationId(conversationId)
        setShowContinueInChat(true)
      }

      client.onMessageSaved = ({ role, content, timestamp, conversationId }) => {
        if (selectedConversation && selectedConversation.id === conversationId) {
          useChatStore.getState().addMessage({
            message_id: `call-${Date.now()}-${Math.random()}`,
            role,
            content,
            timestamp,
          })
        }
      }

      client.onAudioLevel = level => setAudioLevel(level)

      await client.connect()
      setCallClient(client)
      setIsCallActive(true)
      setIsConnecting(false)

      // 4. Start Mic
      await client
        .startMic()
        .then(() => {
          setIsMicOn(true)
          setCallStatus('🎤 Microfono attivo - Parla con FRANCA!')
        })
        .catch(micError => {
          console.error('❌ Microphone start error:', micError)
          setCallStatus('⚠️ Chiamata attiva ma microfono non disponibile')
          setIsMicOn(false)
        })

      return client
    } catch (error) {
      console.error('❌ Start Call Error:', error)

      let errorMessage = error.message || 'Errore connessione sconosciuto'
      if (errorMessage.includes('GEMINI_API_KEY')) {
        errorMessage = "Configurazione server mancante. Contatta l'amministratore."
      }

      setCallStatus(errorMessage)
      setIsCallActive(false)
      setIsConnecting(false)
      releaseCallLock()

      return null
    }
  }, [
    token,
    selectedConversation,
    isCallLockedByOtherTab,
    isConnecting,
    acquireCallLock,
    releaseCallLock,
    setIsConnecting,
    setCallStatus,
    setIsCallActive,
    setIsMicOn,
    setAudioLevel,
    setCallSavedConversationId,
    setShowContinueInChat,
    appendToCallTranscript,
    endCall,
    setCallClient,
  ])

  const startScreenShare = useCallback(async () => {
    if (useCallStore.getState().isStartingScreen) return

    setIsStartingScreen(true)
    setCallStatus('📺 Avvio condivisione schermo...')

    try {
      let client = useCallStore.getState().callClient
      if (!client) {
        client = await startCall()
      }

      if (!client) {
        throw new Error('Impossibile avviare condivisione - connessione non riuscita')
      }

      const isMicActive = useCallStore.getState().isMicOn

      if (!isMicActive) {
        setCallStatus('🎤 Attivazione microfono per condivisione...')
        try {
          await client.startMic()
          setIsMicOn(true)
        } catch (micErr) {
          console.warn('⚠️ Could not auto-start mic during screen share:', micErr)
        }
      }

      await client.startScreenCapture()
      setIsScreenOn(true)
      setCallStatus('🖥️ Condivisione schermo attiva!')
    } catch (e) {
      console.error('❌ Screen share error:', e)
      const msg = e.message || 'Errore configurazione condivisione schermo'
      setCallStatus(`⚠️ ${msg}`)
    } finally {
      setIsStartingScreen(false)
    }
  }, [startCall, setIsStartingScreen, setCallStatus, setIsScreenOn, setIsMicOn])

  const stopScreenShare = useCallback(async () => {
    // === FIX: Get client from STORE ===
    const client = useCallStore.getState().callClient

    if (client) {
      try {
        await client.stopScreenCapture()
      } catch (e) {
        console.error('❌ Error stopping screen share:', e)
      }
    }
    setIsScreenOn(false)
    setCallStatus('📺 Condivisione schermo terminata')
  }, [setIsScreenOn, setCallStatus])

  const toggleMic = useCallback(async () => {
    // === FIX: Get client from STORE ===
    const client = useCallStore.getState().callClient

    if (!client) return

    try {
      // Use getState() to ensure fresh state
      const isMicOn = useCallStore.getState().isMicOn

      if (isMicOn) {
        await client.stopMic()
        setIsMicOn(false)
        setCallStatus('🔇 Microfono disattivato')
      } else {
        setCallStatus('🎤 Attivazione microfono...')
        await client.startMic()
        setIsMicOn(true)
        setCallStatus('🎤 Microfono attivo - Parla con FRANCA!')
      }
    } catch (e) {
      console.error('❌ Mic toggle error:', e)
      setCallStatus('⚠️ Errore controllo microfono')
      setIsMicOn(false)
    }
  }, [setIsMicOn, setCallStatus])

  return {
    startCall,
    startScreenShare,
    stopScreenShare,
    toggleMic,
    endCall,
    // Helper if you need the client in the UI
    getCallClient: () => useCallStore.getState().callClient,
  }
}
